import { produce } from "immer";
import { useCallback, useEffect, useState } from "react";

import { familyName } from "../domain/family";
import type { KayaState } from "../domain/types";
import { hydrate } from "../store/defaults";
import { applyStepDays } from "../store/kayaStore";
import { api, type LinkInfo, type StepDay } from "../sync/api";
import { linkedParts, type LinkedPart } from "../sync/linked";

/*
 * The family view's data (ADR-046): the links, and a linked person's state as the server holds
 * it. Read over the network each time a page opens — the link is authorised per request, and a
 * copy kept on this phone would outlive a link the parent has removed. The last answer is kept
 * in memory only, so moving between the family pages does not flash empty.
 */

let linksCache: LinkInfo[] | null = null;

export function useLinks(): { links: LinkInfo[] | null; error: string | null; reload: () => Promise<void> } {
  const [links, setLinks] = useState<LinkInfo[] | null>(linksCache);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const r = await api.links();
      linksCache = r.links;
      setLinks(r.links);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reach Kaya's server");
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { links, error, reload };
}

export interface Linked {
  linkId: string;
  /** what the page calls them: the reader's word, else their name */
  name: string;
  label: string;
  accountName: string;
  /** their state, hydrated, with their step rows folded in; defaults when their phone never synced */
  S: KayaState;
  /** false until their phone has synced once */
  synced: boolean;
  /** their devices that take Kaya's pushes — a reminder set here reaches none without one */
  devices: number;
  steps: StepDay[];
  /** "Tell me when a week is missed" is on for this link (ADR-056) */
  notifyMissedWeek: boolean;
  /** this reader's devices that take Kaya's pushes — the notice reaches none without one */
  readerDevices: number;
}

const cache = new Map<string, Linked>();

interface Raw {
  label: string;
  accountName: string;
  state: KayaState | null;
  devices: number;
  steps: StepDay[];
  notifyMissedWeek: boolean;
  readerDevices: number;
}

function build(linkId: string, r: Raw): Linked {
  const S = produce(hydrate(r.state ?? {}), (d) => applyStepDays(d, r.steps));
  const name = familyName(r.label, r.state ? S : null, r.accountName);
  return {
    linkId,
    name,
    label: r.label,
    accountName: r.accountName,
    S,
    synced: !!r.state,
    devices: r.devices,
    steps: r.steps,
    notifyMissedWeek: r.notifyMissedWeek,
    readerDevices: r.readerDevices,
  };
}

export interface UseLinked {
  linked: Linked | null;
  error: string | null;
  reload: () => Promise<void>;
  /**
   * Changes their state with the app's own writers and sends only `parts`. Resolves to the parts
   * their phone had changed since and kept — empty when this write stood.
   */
  write: (parts: readonly LinkedPart[], fn: (d: KayaState) => void) => Promise<LinkedPart[]>;
  /** "Tell me when a week is missed", on the link (ADR-056) */
  setNotify: (on: boolean) => Promise<void>;
}

export function useLinked(linkId: string): UseLinked {
  const [linked, setLinked] = useState<Linked | null>(cache.get(linkId) ?? null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.linkState(linkId);
      const next = build(linkId, {
        label: r.person.label,
        accountName: r.person.name || r.person.email,
        state: r.state,
        devices: r.devices,
        steps: r.steps,
        notifyMissedWeek: r.notifyMissedWeek === true,
        readerDevices: r.readerDevices ?? 0,
      });
      cache.set(linkId, next);
      setLinked(next);
      setError(null);
    } catch (e) {
      // A link removed from the other phone reads as gone, not as a network fault.
      cache.delete(linkId);
      setError(e instanceof Error ? e.message : "Could not reach Kaya's server");
    }
  }, [linkId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const write = useCallback(
    async (parts: readonly LinkedPart[], fn: (d: KayaState) => void): Promise<LinkedPart[]> => {
      const cur = cache.get(linkId);
      if (!cur) throw new Error("Their plan is not loaded yet");
      const changed = produce(cur.S, fn);
      // the writers stamp only a real change; nothing changed is nothing to send
      if (changed === cur.S) return [];
      const body = linkedParts(changed, parts);
      if (!Object.keys(body).length) return [];
      const r = await api.linkWrite(linkId, body);
      const next = build(linkId, { ...cur, state: r.state });
      cache.set(linkId, next);
      setLinked(next);
      return r.overruled;
    },
    [linkId]
  );

  const setNotify = useCallback(
    async (on: boolean): Promise<void> => {
      const r = await api.linkNotify(linkId, on);
      const cur = cache.get(linkId);
      if (!cur) return;
      const next = { ...cur, notifyMissedWeek: r.link.notifyMissedWeek === true };
      cache.set(linkId, next);
      setLinked(next);
    },
    [linkId]
  );

  return { linked, error, reload, write, setNotify };
}
